/**
 * Generate a 12-char URL-safe random token for public bundle URLs.
 * ~72 bits of entropy — sufficient for unguessable public links.
 *
 * Uses crypto.getRandomValues with a 64-char alphabet so the modulo is
 * perfectly uniform (256 / 64 = 4).
 */
const TOKEN_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";

export function generatePublicBundleToken(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < 12; i++) out += TOKEN_ALPHABET[bytes[i] & 63];
  return out;
}

/** Current + next 2 quarters as YYYY-QN strings. */
export function listQuarterOptions(now: Date = new Date()): string[] {
  const year = now.getFullYear();
  const month = now.getMonth();
  let y = year;
  let q = Math.floor(month / 3) + 1;
  const opts: string[] = [];
  for (let i = 0; i < 3; i++) {
    opts.push(`${y}-Q${q}`);
    q += 1;
    if (q > 4) {
      q = 1;
      y += 1;
    }
  }
  return opts;
}

/** Format a body_region_focus enum value for display. */
export const BODY_REGION_LABELS: Record<string, string> = {
  neck_shoulders: "Neck & shoulders",
  low_back: "Low back",
  upper_extremity: "Upper extremity",
  lower_extremity: "Lower extremity",
  full_body: "Full body",
};

export function bodyRegionLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return BODY_REGION_LABELS[value] ?? value;
}

/** Public bundle URL for a given token, anchored to the current origin. */
export function publicBundleUrl(token: string): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/play/${token}`;
  }
  return `/play/${token}`;
}

/**
 * Window event fired after a bundle is renamed, so the app header (which loads the
 * title once per route) can pick up the new name without a page reload.
 */
export const BUNDLE_TITLE_CHANGED_EVENT = "f2l:bundle-title-changed";

export type BundleTitleChangedDetail = { id: string; title: string };
